import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarClock, Check, X, ArrowLeft, Loader2, Trash2, RotateCcw, Video } from "lucide-react";
import { api, apiError } from "../lib/api";
import { BrandMark } from "../components/BrandMark";

function browserTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Australia/Sydney"; }
  catch { return "Australia/Sydney"; }
}
function localDayTime(iso, tz) {
  try {
    const dt = new Date(iso);
    const day = new Intl.DateTimeFormat("en-AU", { weekday: "long", day: "numeric", month: "short", timeZone: tz }).format(dt);
    const time = new Intl.DateTimeFormat("en-AU", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz }).format(dt).toLowerCase();
    return `${day} at ${time}`;
  } catch { return null; }
}

/**
 * BookingManage — prospect self-serve page.
 *
 * Public route: `/booking/:token` (linked from the confirmation email).
 * Lets the prospect see their booking, cancel it, or reschedule to a new
 * open slot — without needing to log in or email us.
 */
export default function BookingManage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [days, setDays] = useState([]);
  const [pickedDay, setPickedDay] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [mode, setMode] = useState("view"); // view | reschedule | cancelled
  const [busy, setBusy] = useState(false);
  const [localTz] = useState(browserTz);
  const localSlotLabel = useMemo(() => (booking?.slot_iso ? localDayTime(booking.slot_iso, localTz) : null), [booking?.slot_iso, localTz]);

  useEffect(() => {
    api.get(`/bookings/manage/${token}`).then((r) => {
      setBooking(r.data);
      if (r.data.status === "cancelled") setMode("cancelled");
    }).catch(() => setNotFound(true));
  }, [token]);

  const loadSlots = () => {
    api.get("/bookings/slots").then((r) => {
      const ds = r.data?.days || [];
      setDays(ds);
      const firstOpenDay = ds.find((d) => d.slots.some((s) => !s.booked));
      if (firstOpenDay) setPickedDay(firstOpenDay.day_key);
    });
  };
  useEffect(() => { if (mode === "reschedule") loadSlots(); /* eslint-disable-next-line */ }, [mode]);

  const dayObj = useMemo(() => days.find((d) => d.day_key === pickedDay), [days, pickedDay]);

  const cancel = async () => {
    if (!window.confirm("Cancel this call? You can book a new time from the site any time.")) return;
    setBusy(true);
    try {
      await api.post(`/bookings/manage/${token}/cancel`);
      setBooking((b) => ({ ...b, status: "cancelled" }));
      setMode("cancelled");
      toast.success("Booking cancelled.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const reschedule = async () => {
    if (!pickedSlot) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/manage/${token}/reschedule`, { slot_id: pickedSlot });
      setBooking((b) => ({ ...b, slot_id: data.slot_id, slot_label: data.slot_label }));
      toast.success(`Moved to ${data.slot_label}`);
      setMode("view"); setPickedSlot(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (notFound) return (
    <ManageShell>
      <h1 className="font-display font-extrabold text-[clamp(2rem,3.4vw,2.6rem)] tracking-[-0.02em]">Link no longer valid.</h1>
      <p className="mt-3 text-[15px] text-[#3a3d44] max-w-md">This booking link is no longer valid. The booking may have been deleted. Book a fresh time from the homepage and you'll be right back on track.</p>
      <Link to="/" className="btn-lift mt-8 inline-flex items-center gap-2 bg-[#0B0D10] text-white rounded-full h-11 px-5 font-semibold text-[14px]"><ArrowLeft size={14} />Back to home</Link>
    </ManageShell>
  );
  if (!booking) return <ManageShell><div className="text-[13px] text-[#3a3d44]/60 animate-pulse">Loading your booking…</div></ManageShell>;

  return (
    <ManageShell>
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#3a3d44]/55 mb-3">Your BackPocket booking</div>
      <h1 className="font-display font-extrabold text-[clamp(2rem,3.4vw,2.6rem)] tracking-[-0.02em] leading-[1.02]">
        {mode === "cancelled" ? <>This one's <span className="font-serif-editorial italic font-normal tracking-normal text-[#3a3d44]/70">cancelled.</span></> :
         mode === "reschedule" ? <>Pick a new time.</> :
         <>You're booked in, <span className="font-serif-editorial italic font-normal tracking-normal text-[#3a3d44]/70">{booking.name?.split(" ")[0]}.</span></>}
      </h1>

      {/* Booking card */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.32 }}
        className={`mt-8 rounded-3xl p-6 sm:p-8 ${mode === "cancelled" ? "bg-white border border-[#E4E0D6]" : "bg-[#0B0D10] text-white"} relative overflow-hidden`}
        data-testid="booking-summary">
        {mode !== "cancelled" && <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#C8FF4D]/12 blur-3xl pointer-events-none" />}
        <div className="relative z-10 flex items-center gap-4">
          <div className={`h-11 w-11 rounded-full grid place-items-center shrink-0 ${mode === "cancelled" ? "bg-[#EFEBE1] text-[#3a3d44]" : "bg-[#C8FF4D] text-[#0B0D10]"}`}>
            <CalendarClock size={18} strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className={`text-[11px] uppercase tracking-[0.14em] ${mode === "cancelled" ? "text-[#3a3d44]/55" : "text-[#C8FF4D]"}`}>Your slot</div>
            <div className={`font-display font-bold text-[18px] tracking-[-0.01em] mt-0.5 ${mode === "cancelled" ? "line-through text-[#3a3d44]/60" : "text-white"}`}>{localSlotLabel || booking.slot_label}</div>
            {localSlotLabel && mode !== "cancelled" && <div className="text-[11.5px] text-white/50 mt-1">{booking.slot_label}</div>}
          </div>
        </div>
        {booking.zoom_url && mode !== "cancelled" && (
          <div className="relative z-10 mt-4 border-t border-white/10 pt-4">
            <a href={booking.zoom_url} target="_blank" rel="noreferrer" data-testid="manage-zoom-link"
              className="btn-lift inline-flex items-center gap-2 bg-[#C8FF4D] text-[#0B0D10] rounded-full h-10 px-4 font-semibold text-[13px]">
              <Video size={14} strokeWidth={2.4} /> Join Zoom
            </a>
            <div className="mt-2 text-[11.5px] text-white/45 break-all font-mono">{booking.zoom_url}</div>
          </div>
        )}
      </motion.div>

      {mode === "view" && (
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button data-testid="manage-reschedule-btn" onClick={() => setMode("reschedule")}
            className="btn-lift bg-[#C8FF4D] text-[#0B0D10] rounded-full h-11 px-5 font-semibold text-[14px] inline-flex items-center gap-2"><RotateCcw size={14} />Reschedule</button>
          <button data-testid="manage-cancel-btn" disabled={busy} onClick={cancel}
            className="rounded-full h-11 px-5 font-medium text-[14px] inline-flex items-center gap-2 border border-[#E4E0D6] hover:border-[#0B0D10]/40 text-[#3a3d44] hover:text-[#0B0D10]"><Trash2 size={13} />Cancel booking</button>
        </div>
      )}

      {mode === "reschedule" && (
        <div className="mt-8">
          {/* Day scroller */}
          <div className="-mx-6 sm:-mx-0 px-6 sm:px-0 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 pb-2 min-w-max">
              {days.map((d) => {
                const anyOpen = d.slots.some((s) => !s.booked);
                const on = d.day_key === pickedDay;
                const [dow, dnum, mon] = d.day_label.split(" ");
                return (
                  <button key={d.day_key} data-testid={`manage-day-${d.day_key}`} disabled={!anyOpen}
                    onClick={() => { setPickedDay(d.day_key); setPickedSlot(null); }}
                    className={`shrink-0 w-[72px] rounded-2xl border transition-all py-3 text-center ${
                      on ? "bg-[#0B0D10] border-[#0B0D10] text-white"
                        : anyOpen ? "bg-white border-[#E4E0D6] hover:border-[#0B0D10]/40 text-[#0B0D10]"
                        : "bg-white/40 border-[#E4E0D6] text-[#3a3d44]/30 cursor-not-allowed"
                    }`}>
                    <div className={`text-[10.5px] uppercase tracking-[0.14em] ${on ? "text-[#C8FF4D]" : "text-[#3a3d44]/60"}`}>{dow}</div>
                    <div className="font-display font-extrabold tracking-[-0.02em] text-[20px] leading-none mt-1">{dnum}</div>
                    <div className={`text-[10.5px] mt-1 ${on ? "text-white/60" : "text-[#3a3d44]/55"}`}>{mon}</div>
                  </button>
                );
              })}
            </div>
          </div>
          {dayObj ? (
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="manage-slots">
              {dayObj.slots.map((s) => {
                const on = s.id === pickedSlot;
                const isCurrent = s.id === booking.slot_id;
                return (
                  <button key={s.id} data-testid={`manage-slot-${s.id}`}
                    disabled={s.booked && !isCurrent}
                    onClick={() => setPickedSlot(s.id)}
                    className={`h-11 rounded-full text-[13px] font-medium tabular-nums transition-all ${
                      isCurrent ? "bg-white border-2 border-[#0B0D10] text-[#0B0D10]"
                        : s.booked ? "bg-white/40 text-[#3a3d44]/30 line-through cursor-not-allowed"
                        : on ? "bg-[#C8FF4D] text-[#0B0D10] shadow-[0_8px_18px_-6px_rgba(200,255,77,0.75)]"
                        : "bg-white border border-[#E4E0D6] text-[#0B0D10] hover:border-[#0B0D10]/40"
                    }`}>{s.label}{isCurrent && " · now"}</button>
                );
              })}
            </div>
          ) : <div className="mt-4 rounded-2xl border-2 border-dashed border-[#E4E0D6] p-8 text-center text-[13px] text-[#3a3d44]/55">Pick a day above.</div>}
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <button data-testid="manage-confirm-reschedule" disabled={!pickedSlot || pickedSlot === booking.slot_id || busy} onClick={reschedule}
              className="btn-lift bg-[#0B0D10] text-white rounded-full h-11 px-5 font-semibold text-[14px] disabled:opacity-50 inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}Confirm new time
            </button>
            <button onClick={() => setMode("view")} className="text-[13px] text-[#3a3d44] hover:text-[#0B0D10] inline-flex items-center gap-1.5"><ArrowLeft size={13} />Back</button>
          </div>
        </div>
      )}

      {mode === "cancelled" && (
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button onClick={() => nav("/")} className="btn-lift bg-[#0B0D10] text-white rounded-full h-11 px-5 font-semibold text-[14px] inline-flex items-center gap-2"><ArrowLeft size={14} />Back to home</button>
          <div className="text-[13px] text-[#3a3d44]/70">We've told the team. No follow-up needed.</div>
        </div>
      )}
    </ManageShell>
  );
}

function ManageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#F4F1E9] text-[#0B0D10]">
      <header className="max-w-[880px] mx-auto px-6 pt-8">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <BrandMark size={30} />
          <span className="font-display font-extrabold text-[19px] tracking-tight">BackPocket</span>
        </Link>
      </header>
      <main className="max-w-[880px] mx-auto px-6 py-14">{children}</main>
    </div>
  );
}
